import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Grid, Alert, TextField, InputAdornment, Paper, Tooltip,
  IconButton, Menu, MenuItem, Checkbox, ListItemText
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { Link as MuiLink } from '@mui/material';
import { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import dayjs, { Dayjs } from 'dayjs';
import StatCard from '../components/StatCard';
import DataTable from '../components/DataTable';
import MonthRangePicker from '../components/MonthRangePicker';
import { useAdminApi } from '../hooks/useAdminApi';
import { OrganizationBillingDetailResponse, OrganizationBillingUserSummary } from '../types/adminApiTypes';
import { formatNumber } from '../utils/helpers';

const USER_TYPE_OPTIONS = [
  { label: 'All Types', value: '' },
  { label: 'Admin', value: 'admin' },
  { label: 'Standard', value: 'standard' },
  { label: 'API', value: 'api' },
];

const OrganizationBillingDetailView: React.FC = () => {
  const { orgName } = useParams<{ orgName: string }>();
  const { data, loading, error, fetchData } = useAdminApi<OrganizationBillingDetailResponse>();

  // Month range state (default: current month)
  const [startMonth, setStartMonth] = useState<Dayjs | null>(dayjs().subtract(1, 'year').startOf('month'));
  const [endMonth, setEndMonth] = useState<Dayjs | null>(dayjs().startOf('month'));

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'email', sort: 'asc' }]);
  const cursorCacheRef = useRef<Record<number, string>>({});
  const lastFetchedPageRef = useRef<number>(0);
  // Store fetchData in a ref so auth token refreshes inside useAdminApi don't
  // change the function reference and trigger an unintended re-fetch.
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  // inputValue: what the user is currently typing; submittedSearch: last committed value sent to API
  const [inputValue, setInputValue] = useState<string>('');
  const [submittedSearch, setSubmittedSearch] = useState<string>('');
  const isPendingSearch = inputValue !== submittedSearch;
  const [selectedUserTypes, setSelectedUserTypes] = useState<string[]>([]);
  const [typeFilterAnchor, setTypeFilterAnchor] = useState<HTMLElement | null>(null);

  // Keep rowCount stable while loading so MUI DataGrid doesn't auto-reset the
  // page to 0 when data becomes null mid-fetch (which would trigger a second
  // no-cursor fetch, corrupting cursor-based pagination).
  const [rowCountState, setRowCountState] = useState(0);
  useEffect(() => {
    setRowCountState(prev => data?.total_users !== undefined ? data.total_users : prev);
  }, [data?.total_users]);

  const displayOrgName = orgName ? decodeURIComponent(orgName) : '';

  const handleTypeFilterClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setTypeFilterAnchor(event.currentTarget);
  }, []);

  const handleTypeFilterClose = useCallback(() => {
    setTypeFilterAnchor(null);
  }, []);

  const handleTypeSelect = useCallback((value: string) => {
    if (value === '') {
      setSelectedUserTypes([]);
    } else {
      setSelectedUserTypes(prev =>
        prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
      );
    }
  }, []);

  const columns = useMemo((): GridColDef<OrganizationBillingUserSummary>[] => [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.5,
      renderCell: (params) => (
        <MuiLink component={RouterLink} to={`/dashboard/users/${params.row.user_id}`}>
          {params.value}
        </MuiLink>
      ),
    },
    {
      field: 'user_type',
      headerName: 'Type',
      width: 100,
      sortable: false,
      renderHeader: () => (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box sx={{ flexGrow: 1, fontWeight: 500, fontSize: '0.875rem' }}>
            Type
          </Box>
          <Tooltip title={selectedUserTypes.length > 0 ? `Filtered: ${selectedUserTypes.join(', ')}` : 'Filter by type'}>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); handleTypeFilterClick(e); }}
              sx={{ p: 0.25, color: selectedUserTypes.length > 0 ? 'primary.main' : 'action.active' }}
              aria-label="Filter by type"
            >
              <MoreVertIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'total_costs',
      headerName: 'Total Costs',
      type: 'number',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        const numValue = value as number;
        if (numValue === null || numValue === undefined) return '$0.00';
        return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      field: 'total_openai_costs',
      headerName: 'OpenAI Costs',
      type: 'number',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        const numValue = value as number;
        if (numValue === null || numValue === undefined) return '$0.00';
        return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      field: 'total_google_costs',
      headerName: 'Google Costs',
      type: 'number',
      width: 140,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        const numValue = value as number;
        if (numValue === null || numValue === undefined) return '$0.00';
        return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
    {
      field: 'total_other_llm_costs',
      headerName: 'Other LLM Costs',
      type: 'number',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => {
        const numValue = value as number;
        if (numValue === null || numValue === undefined) return '$0.00';
        return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      },
    },
  ], [selectedUserTypes, handleTypeFilterClick]);

  // Reset cursor cache when org, date range, committed search term, or type filter changes.
  useEffect(() => {
    cursorCacheRef.current = {};
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [orgName, startMonth, endMonth, submittedSearch, selectedUserTypes]);

  useEffect(() => {
    if (!orgName || !startMonth?.isValid() || !endMonth?.isValid()) return;

    lastFetchedPageRef.current = paginationModel.page;
    const decodedOrgName = decodeURIComponent(orgName);

    const params: Record<string, any> = {
      endpoint: `/billing/${decodedOrgName}/costs`,
      start_date: startMonth.startOf('month').format('YYYY-MM-DD'),
      end_date: endMonth.endOf('month').format('YYYY-MM-DD'),
      page_size: paginationModel.pageSize,
    };

    const cursor = paginationModel.page > 0
      ? cursorCacheRef.current[paginationModel.page]
      : undefined;
    if (cursor) params.cursor = cursor;

    if (sortModel.length > 0) {
      params.sort_by = sortModel[0].field;
      if (sortModel[0].sort) params.sort_order = sortModel[0].sort;
    }

    if (submittedSearch) {
      params.search_term = submittedSearch;
    }

    if (selectedUserTypes.length > 0) {
      params.user_type = selectedUserTypes;
    }

    fetchDataRef.current(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgName, startMonth, endMonth, paginationModel.page, paginationModel.pageSize, sortModel, submittedSearch, selectedUserTypes]);

  useEffect(() => {
    if (data?.next_cursor) {
      cursorCacheRef.current[lastFetchedPageRef.current + 1] = data.next_cursor;
    }
  }, [data]);

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    if (newModel.pageSize !== paginationModel.pageSize) {
      cursorCacheRef.current = {};
      setPaginationModel({ page: 0, pageSize: newModel.pageSize });
    } else if (newModel.page !== paginationModel.page) {
      setPaginationModel(newModel);
    }
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    cursorCacheRef.current = {};
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setSortModel(newSortModel);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setSubmittedSearch(inputValue);
    }
  };

  if (!orgName) {
    return <Alert severity="warning">Organization name not found in URL.</Alert>;
  }

  if (error) {
    return <Alert severity="error">Error loading billing data: {error}</Alert>;
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Page Header */}
      <Typography variant="h5" gutterBottom component="div">
        {displayOrgName} Billing Detail
      </Typography>

      {/* Month Range Picker */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Select Month Range
        </Typography>
        <MonthRangePicker
          startMonth={startMonth}
          endMonth={endMonth}
          onStartMonthChange={setStartMonth}
          onEndMonthChange={setEndMonth}
        />
      </Paper>

      {/* Summary Tiles Row 1: LLM Provider Costs */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Costs"
            value={data?.summary?.total_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="OpenAI Costs"
            value={data?.summary?.total_openai_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Google Costs"
            value={data?.summary?.total_google_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Other LLM Costs"
            value={data?.summary?.total_other_llm_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Summary Tiles Row 2: Usage-Based Costs */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Users"
            value={data?.summary?.total_users}
            icon={<PeopleIcon fontSize="large" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="User Costs"
            value={data?.summary?.total_user_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="API Costs"
            value={data?.summary?.total_api_costs}
            icon={<AttachMoneyIcon fontSize="large" />}
            formatType="currency"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* User Cost Breakdown Section */}
      <Box sx={{ mt: 4 }}>
        {/* Title and Search Bar Row */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2
        }}>
          <Typography variant="h6" component="div">
            {displayOrgName} User Costs
          </Typography>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by email…"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{ width: { xs: '100%', sm: '300px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: isPendingSearch ? (
                <InputAdornment position="end">
                  <Tooltip title="Press Enter to search">
                    <KeyboardReturnIcon fontSize="small" color="action" />
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>

        <DataTable
          rows={data?.users || []}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.user_id}
          rowCount={rowCountState}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          paginationMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          sortingMode="server"
        />
      </Box>

      {/* Type Filter Menu */}
      <Menu
        anchorEl={typeFilterAnchor}
        open={Boolean(typeFilterAnchor)}
        onClose={handleTypeFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ elevation: 2, sx: { minWidth: 140 } }}
      >
        {USER_TYPE_OPTIONS.map(({ label, value }) => (
          <MenuItem
            key={value || 'all'}
            onClick={() => handleTypeSelect(value)}
            dense
          >
            <Checkbox
              size="small"
              checked={value === '' ? selectedUserTypes.length === 0 : selectedUserTypes.includes(value)}
              sx={{ p: 0.5, mr: 0.5 }}
            />
            <ListItemText primary={label} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default OrganizationBillingDetailView;
